import NodeCache from 'node-cache';
import sendHttpReq, { SendHttpResponse } from './http';
import APIS from '../../constants/APIS';
import throwTypedError from '../shared/throwTypedError';
import { requestLogger } from './loggers';
import ERROR_TYPES from '../../constants/SERVICE_ERROR_TYPES';

const consentCache = new NodeCache();
const consentCacheKey = (contractType, version) => `consent_contract_${contractType}_${version}`;

interface GetTerms {
  candidateId: number | string;
  transactionId: string;
  contractType?: string;
  version?: string;
}

interface GetTermsResponse {
  url: string;
  hash: string;
  version: string;
  id: number;
  content: any;
}

const getTerms = ({
  candidateId, transactionId, contractType, version = 'latest',
}: GetTerms):Promise<GetTermsResponse> | GetTermsResponse => {
  let message: string;

  const requestOpts = {
    method: 'get',
    url: APIS.CONSENTS.TERMS_AND_CONDITIONS_URL(contractType, version),
    headers: {
      'x-api-key': APIS.CONSENTS.API_KEY,
      'x-transaction-id': transactionId,
    },
  };

  const consentData = consentCache.get(consentCacheKey(contractType, version)) as GetTermsResponse;
  if (consentData && consentData.content) {
    return consentData;
  }

  return sendHttpReq(requestOpts).catch((error) => {
    message = `Failed to fetch consent term: ${error.message}`;

    requestLogger.error({
      message,
      candidateId,
      transactionId: error.transactionId,
    });

    throwTypedError(message, ERROR_TYPES.FETCH_FAILURE);
  }).then((res: SendHttpResponse) => {
    if (res.status !== 200) {
      message = `Failed to fetch consent term, received status ${res.status}: ${JSON.stringify(res.data)}`;

      requestLogger.error({
        message,
        statusCode: res.status,
        candidateId,
        transactionId: res.transactionId,
      });

      throwTypedError(
        message,
        (res.status === 401 || res.status === 404) ? ERROR_TYPES.NOT_FOUND : ERROR_TYPES.FETCH_FAILURE,
      );

      return null;
    }

    res.data.id = APIS.CONSENTS.TERM_UUID(contractType);

    consentCache.set(consentCacheKey(contractType, version), res.data, 3600);

    return res.data;
  });
};

const getContract = ({
  candidateId, transactionId = '', contractType, version = 'latest',
}: GetTerms): Promise<GetTermsResponse>  | GetTermsResponse => {
  let message: string;

  const requestOpts = {
    method: 'get',
    url: APIS.CONSENTS.CONTRACT_URL(contractType, version),
    headers: {
      'x-api-key': APIS.CONSENTS.API_KEY,
      'x-transaction-id': transactionId,
    },
  };

  const consentData = consentCache.get(consentCacheKey(contractType, version)) as GetTermsResponse;
  if (consentData && consentData.content) {
    return consentData;
  }

  return sendHttpReq(requestOpts).catch((error) => {
    message = `Failed to fetch contract: ${error.message}`;

    requestLogger.error({
      message,
      candidateId,
      transactionId: error.transactionId,
    });

    throwTypedError(message, ERROR_TYPES.FETCH_FAILURE);
  }).then((res: SendHttpResponse) => {
    if (res.status !== 200) {
      message = `Failed to fetch contract, received status ${res.status}: ${JSON.stringify(res.data)}`;

      requestLogger.error({
        message,
        statusCode: res.status,
        candidateId,
        transactionId: res.transactionId,
      });

      throwTypedError(
        message,
        (res.status === 401 || res.status === 404) ? ERROR_TYPES.NOT_FOUND : ERROR_TYPES.FETCH_FAILURE,
      );

      return null;
    }

    res.data.id = APIS.CONSENTS.CONTRACT_UUID(contractType);

    consentCache.set(consentCacheKey(contractType, version), res.data, 3600);

    return res.data;
  });
};


interface AcceptTerms {
  candidateId: number;
  sessionToken: string;
  termId: string;
  termVersion: string;
  transactionId: string;
}

const acceptTerms = ({
  candidateId,
  sessionToken,
  termId,
  termVersion,
  transactionId,
}: AcceptTerms) => {
  let message: string;

  const requestOpts = {
    method: 'post',
    url: APIS.CONSENTS.ACCEPT_URL,
    data: {
      termId,
      termVersion,
      action: 'consenting',
    },
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'x-api-key': APIS.CONSENTS.API_KEY,
      'x-transaction-id': transactionId,
    },
  };

  return sendHttpReq(requestOpts).catch((error) => {
    message = `Failed to accept consent term: ${error.message}`;

    requestLogger.error({
      message,
      candidateId,
      transactionId: error.transactionId || transactionId,
    });

    throwTypedError(message, ERROR_TYPES.FETCH_FAILURE);
  }).then((res: SendHttpResponse) => {
    if (!(res.status >= 200 && res.status < 300)) {
      message = `Failed to accept consent term ${termId}__${termVersion}, received status ${res.status}: ${JSON.stringify(res.data)}`;

      requestLogger.error({
        message,
        statusCode: res.status,
        candidateId,
        transactionId: res.transactionId || transactionId,
      });

      throwTypedError(
        message,
        (res.status === 401 || res.status === 404) ? ERROR_TYPES.NOT_FOUND : ERROR_TYPES.FETCH_FAILURE,
      );

      return null;
    }

    return res.data;
  });
};

const acceptContract = ({
  candidateId,
  sessionToken,
  termId,
  termVersion,
  transactionId,
}: AcceptTerms) => {
  let message: string;

  const requestOpts = {
    method: 'post',
    url: APIS.CONSENTS.ACCEPT_URL,
    data: {
      termId,
      termVersion,
      action: 'consenting',
    },
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'x-api-key': APIS.CONSENTS.API_KEY,
      'x-transaction-id': transactionId,
    },
  };

  return sendHttpReq(requestOpts).catch((error) => {
    message = `Failed to accept contract: ${error.message}`;

    requestLogger.error({
      message,
      candidateId,
      transactionId: error.transactionId || transactionId,
    });

    throwTypedError(message, ERROR_TYPES.FETCH_FAILURE);
  }).then((res: SendHttpResponse) => {
    if (!(res.status >= 200 && res.status < 300)) {
      message = `Failed to accept contract, received status ${res.status}: ${JSON.stringify(res.data)}`;

      requestLogger.error({
        message,
        statusCode: res.status,
        candidateId,
        transactionId: res.transactionId || transactionId,
      });

      throwTypedError(
        message,
        res.status === 404 ? ERROR_TYPES.NOT_FOUND : ERROR_TYPES.FETCH_FAILURE,
      );

      return null;
    }

    return res.data;
  });
};

interface HasAcceptedTermVersion {
  candidateId: string;
  transactionId: string;
  termVersion: string;
  contractType: string;
  sessionToken: string;
}

const hasAcceptedTermVersion = ({
  candidateId,
  sessionToken,
  transactionId,
  termVersion,
  contractType,
}: HasAcceptedTermVersion) => {
  let message: string;

  const requestOpts = {
    method: 'get',
    url: APIS.CONSENTS.VERSION_URL(contractType, termVersion),
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'x-api-key': APIS.CONSENTS.API_KEY,
      'x-transaction-id': transactionId,
    },
  };

  return sendHttpReq(requestOpts).catch((error) => {
    message = `Failed to fetch term version: ${error.message}`;

    requestLogger.error({
      message,
      candidateId,
      transactionId: error.transactionId,
    });

    throwTypedError(message, ERROR_TYPES.FETCH_FAILURE);
  }).then((res: SendHttpResponse) => {
    if (res.status !== 200) {
      message = `Failed to fetch term version ${contractType}__${termVersion}, received status ${res.status}: ${JSON.stringify(res.data)}`;

      requestLogger.error({
        message,
        statusCode: res.status,
        candidateId,
        transactionId: res.transactionId,
      });

      return false;
    }

    return (res.data[0].termVersion === termVersion) && res.data[0].hasAccepted;
  });
};

interface HasAcceptedContractVersion {
  candidateId: string;
  transactionId: string;
  contractVersion: string;
  contractType: string;
  sessionToken: string;
}

const hasAcceptedContractVersion = ({
  candidateId,
  sessionToken,
  transactionId,
  contractVersion,
  contractType,
}:HasAcceptedContractVersion ) => {
  let message: string;

  const requestOpts = {
    method: 'get',
    url: APIS.CONSENTS.VERSION_URL(contractType, contractVersion),
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      'x-api-key': APIS.CONSENTS.API_KEY,
      'x-transaction-id': transactionId,
    },
  };

  return sendHttpReq(requestOpts).catch((error) => {
    message = `Failed to fetch contract version: ${error.message}`;

    requestLogger.error({
      message,
      candidateId,
      transactionId: error.transactionId,
    });

    throwTypedError(message, ERROR_TYPES.FETCH_FAILURE);
  }).then((res: SendHttpResponse) => {
    if (res.status !== 200) {
      message = `Failed to fetch contract version, received status ${res.status}: ${JSON.stringify(res.data)}`;

      requestLogger.error({
        message,
        statusCode: res.status,
        candidateId,
        transactionId: res.transactionId,
      });

      return false;
    }

    return (res.data[0].termVersion === contractVersion) && res.data[0].hasAccepted;
  });
};

export {
  getTerms,
  getContract,
  acceptTerms,
  acceptContract,
  hasAcceptedTermVersion,
  hasAcceptedContractVersion,
};
